import { Input } from '@rebass/forms';
import useTranslation from 'next-translate/useTranslation';
import PropTypes from 'prop-types';
import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useMutation, useQueryClient } from 'react-query';
import { Box, Button, Flex } from 'rebass/styled-components';

import Card from '@/components/Card';
import Checklist from '@/components/Icon/Checklist';
import UploadLoadingIcon from '@/components/Icon/UploadLoadingIcon';
import Text from '@/components/Text';
import { toast } from '@/components/Toaster/Toaster';
import { userApi } from '@/services';
import ProgressBar from '@/widgets/Dropzone/ProgressBar';

import { UploadIcon } from '../../components/Icon';
import TrustNotice from '../ReconciliationCustomer/TrustNotice';

const maxSize = 5242880;

const colorMap = {
  idle: 'white',
  loading: 'white',
  error: 'red',
  success: 'green',
};

const errorMap = {
  'file-invalid-type': 'wrongFormat',
  'file-too-large': 'maxSize',
};

const Success = ({ text }) => (
  <Flex justifyContent="center" alignItems="center" height="100%">
    <Box pr="16px">
      <Checklist />
    </Box>
    <Box>
      <Text color="darkGreen">{text}</Text>
    </Box>
  </Flex>
);

Success.propTypes = {
  text: PropTypes.string.isRequired,
};

const Error = ({ content: { title, error, retry }, onClick, isWidget }) => (
  <Flex
    justifyContent={isWidget ? 'center' : ['center', 'space-between']}
    alignItems="center"
    height="100%"
    width="100%"
    flexDirection={isWidget ? 'column' : ['column', 'row']}
  >
    <Box textAlign={isWidget ? 'center' : ['center', 'left']} fontSize="14px" color="red">
      <Text lineHeight="24px" fontWeight="bold">
        {title}
      </Text>
      <Text lineHeight="24px" color="darkGrey">
        {error}
      </Text>
    </Box>

    <Box mt={isWidget ? '20px' : ['20px', '0']}>
      <Button onClick={onClick}>{retry}</Button>
    </Box>
  </Flex>
);

Error.propTypes = {
  onClick: PropTypes.func.isRequired,
  content: PropTypes.shape({
    title: PropTypes.string.isRequired,
    error: PropTypes.string.isRequired,
    retry: PropTypes.string.isRequired,
  }).isRequired,
  isWidget: PropTypes.bool.isRequired,
};

const Loading = ({ progress, onCancel, isWidget }) => (
  <Flex height="100%" alignItems="center" justifyContent="center" width="100%">
    <Box as={UploadLoadingIcon} width={60} height={30} pr="16px" />

    <ProgressBar value={progress} onCancel={onCancel} isWidget={isWidget} maxWidth="350px" />
  </Flex>
);

const WidgetLayout = ({ children }) => {
  return (
    <>
      <Box width="47px" height="34px" mb="10px">
        <UploadIcon width="100%" height="100%" />
      </Box>
      <Text textAlign="center" lineHeight="24px" variant="body" fontSize={[14, 14]}>
        {children}
      </Text>
    </>
  );
};

WidgetLayout.propTypes = {
  children: PropTypes.node,
};

WidgetLayout.defaultProps = {
  children: null,
};

const DocumentCenterLayout = ({ children }) => {
  return (
    <Flex height="100%" flexDirection="row" alignItems="center" justifyContent="center">
      <Box flexShrink="0" width={['38px', '48px', '48px']} height={['26px', '33px', '33px']} mr={['7', '6', '6']}>
        <UploadIcon width="100%" height="100%" />
      </Box>
      <Box>
        <Text lineHeight="24px" variant="body">
          {children}
        </Text>
      </Box>
    </Flex>
  );
};

DocumentCenterLayout.propTypes = {
  children: PropTypes.node,
};

DocumentCenterLayout.defaultProps = {
  children: null,
};

Loading.propTypes = {
  onCancel: PropTypes.func.isRequired,
  progress: PropTypes.number.isRequired,
  isWidget: PropTypes.bool.isRequired,
};

const transformString = (text) =>
  text?.split?.(/(\{.*?})/).map((part) => {
    if (part.includes('{')) {
      const cleanText = part.replace('{', '').replace('}', '');
      return (
        <Text key={cleanText} color="primary" as="span">
          {cleanText}
        </Text>
      );
    }

    return part;
  });

const Dropzone = React.forwardRef(({ isWidget, requestId, onUploadSuccess, projectId, taskId, sid }, ref) => {
  const { t } = useTranslation('page-dashboard');
  const dropzone = t('DocumentTile.Dropzone', {}, { returnObjects: true });
  const { t: m } = useTranslation('common-toast');
  const toaster = m('toast', {}, { returnObjects: true });
  const queryClient = useQueryClient();

  // idle, loading, error, success
  const [state, setState] = useState('idle');
  const [error, setError] = useState('');
  const [uploadProgress, setUploadProgress] = React.useState(0);

  const { mutateAsync } = useMutation(
    async (formData) => {
      setState('loading');

      const response = await userApi.post('/documents', formData, {
        onUploadProgress(progressEvent) {
          setUploadProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
        },
      });

      if (requestId) {
        await userApi.post(
          '/documents/actions/assigndocument',
          {},
          {
            params: { proceedingId: requestId, documentId: response?.data?.data?.documentId },
          }
        );
      }

      if (projectId) {
        await userApi.post(
          `/documents/projectrelation/${projectId}/task/${taskId}/subtask/${sid}`,
          {},
          {
            params: { id_document: response?.data?.data?.documentId },
          }
        );
      }

      return response?.data?.data;
    },
    {
      onSuccess: (data) => {
        onUploadSuccess?.(data?.documentId);

        queryClient.invalidateQueries('documents');
        queryClient.invalidateQueries(['request', requestId]);

        setState('success');
        setUploadProgress(0);

        setTimeout(() => {
          setState('idle');
        }, 3000);
        toast({
          type: 'success',
          message: toaster.documents.upload,
          dismissable: true,
          withIcon: true,
        });
      },
      onError: () => {
        setError(dropzone?.actionStates.error.errors.generic);
        setState('error');
      },
    }
  );

  const onDrop = useCallback(
    async (acceptedFiles) => {
      if (acceptedFiles.length) {
        const formData = new FormData();
        formData.append('file', acceptedFiles[0]);

        await mutateAsync(formData);
      }
    },
    [mutateAsync]
  );

  const onDropRejected = useCallback(
    (event) => {
      const errorCode = event[0].errors[0].code;
      const errorMessage = dropzone?.actionStates.error.errors[errorMap[errorCode] ?? 'generic'];
      setError(errorMessage);
      setState('error');
    },
    [dropzone?.actionStates?.error?.errors]
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    onDropRejected,
    accept: '.pdf,.jpg,.jpeg,.png',
    maxSize,
    multiple: false,
    disabled: state !== 'idle',
  });

  React.useImperativeHandle(ref, () => ({
    open: () => open(),
  }));

  const onCancel = () => {
    setState('idle');
    // TODO: Cancel upload
  };

  const handleRetry = () => {
    setState('idle');
    setError('');
  };

  const backgroundColor = state === 'idle' && isDragActive ? 'backgroundGrey' : `dropzone.${colorMap[state]}`;

  return (
    <>
      <Card
        sx={{
          cursor: 'pointer',
          ':focus': {
            outline: 'none',
          },
        }}
        alignItems="center"
        border="1px dashed darkGrey"
        boxShadow="none"
        flexDirection={isWidget ? 'column' : 'row'}
        justifyContent={isWidget ? 'none' : 'center'}
        py="28px"
        backgroundColor={backgroundColor}
        {...getRootProps()}
      >
        <Input {...getInputProps()} />

        {state === 'idle' ? (
          <>
            {isWidget ? (
              <WidgetLayout>{transformString(dropzone.idle)}</WidgetLayout>
            ) : (
              <DocumentCenterLayout>{transformString(dropzone.idle)}</DocumentCenterLayout>
            )}
          </>
        ) : null}

        {state === 'loading' ? <Loading progress={uploadProgress} onCancel={onCancel} isWidget={isWidget} /> : null}

        {state === 'error' ? (
          <Error
            content={{
              title: 'Hochladen fehlgeschlagen',
              retry: 'nochmal versuchen',
              error,
            }}
            onClick={handleRetry}
            isWidget={isWidget}
          />
        ) : null}

        {state === 'success' ? <Success text="Dokument erfolgreich ins Dokumentencenter hochgeladen." /> : null}
      </Card>

      <Box mt="5">
        <Flex
          flexDirection={isWidget ? ['column', 'column', 'column'] : ['column', 'row', 'row']}
          justifyContent="space-between"
        >
          <Box width={isWidget ? ['100%', '100%', '100%'] : ['100%', '100%', '70%']}>
            <Text textAlign={isWidget ? ['center', 'center', 'center'] : ['center', 'left', 'left']} variant="small">
              {dropzone.infoText}
            </Text>
          </Box>
          <Flex
            justifyContent={isWidget ? ['center', 'center', 'center'] : ['center', 'center', 'flex-end']}
            mt={isWidget ? ['1.25rem', '1.25rem', '1.25rem'] : ['1rem', '1rem', '0']}
            width={isWidget ? ['100%', '100%', '100%'] : ['100%', '100%', '30%']}
          >
            <TrustNotice />
          </Flex>
        </Flex>
      </Box>
    </>
  );
});

Dropzone.propTypes = {
  isWidget: PropTypes.bool,
  onUploadSuccess: PropTypes.func,
  projectId: PropTypes.string.isRequired,
  requestId: PropTypes.string,
  sid: PropTypes.string.isRequired,
  taskId: PropTypes.string.isRequired,
};

Dropzone.defaultProps = {
  isWidget: false,
  requestId: undefined,
  onUploadSuccess: undefined,
};

export default Dropzone;
